import OnboardingLayout from "component/auth/OnboardingLayout";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import {
    handleFetchKybRequirementsSummary,
    handleRequestKybVerification,
} from "api/onboarding";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const steps = [
    { title: "Business details" },
    { title: "Contact details" },
    { title: "Compliance" },
    { title: "Wallets" },
    { title: "Control person" },
    { title: "Directors" },
];

const emptyValue = "Not provided";

const normalizeKey = (value = "") => String(value).toLowerCase().replace(/[-\s]/g, "_");

const getResponseBody = (response) => response?.data || response;

const unwrapValue = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value) && "value" in value) {
        return value.value;
    }

    return value;
};

const formatAddress = (address) => {
    if (!address) return emptyValue;

    return [
        address.houseNumber,
        address.street,
        address.subdivision,
        address.city,
        address.state,
        address.zipCode || address.postalCode,
    ]
        .filter(Boolean)
        .join(", ");
};

const getDocumentUrl = (document) =>
    document?.url || document?.link || document?.document?.url || document?.document?.link || "";

const getDocumentLabel = (document) =>
    document?.key ||
    document?.name ||
    document?.type ||
    document?.document?.key ||
    document?.document?.name ||
    document?.document?.type ||
    document?.url ||
    emptyValue;

const DocumentLink = ({ document, fallbackUrl }) => {
    if (!document && !fallbackUrl) return emptyValue;

    const url = getDocumentUrl(document) || fallbackUrl;
    const label = getDocumentLabel(document);

    if (!url) return label;

    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="aku-body-normal-medium text-[#6F00FF] underline decoration-[#6F00FF]/40 underline-offset-4 hover:text-[#5200BF]"
        >
            {label}
        </a>
    );
};

const formatDocument = (document) => <DocumentLink document={document} />;

const formatName = (person) =>
    `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || emptyValue;

const formatOwnership = (person) =>
    person?.percentageOwnership || person?.ownershipShare || person?.ownership || emptyValue;

const formatIdentificationType = (person) =>
    person?.meanOfIdentificationType ||
    person?.meansOfID ||
    person?.identificationType ||
    person?.idType ||
    emptyValue;

const formatIdentificationNumber = (person) =>
    person?.idNumber ||
    person?.identificationNumber ||
    person?.meanOfIdentificationNumber ||
    emptyValue;

const formatPersonAddress = (person) =>
    formatAddress(person?.address || person?.residentialAddress);

const hasDocument = (document, fallbackUrl) => Boolean(document || fallbackUrl);

const isBusinessRecord = (value) =>
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("_id" in value || typeof value.name === "string" || typeof value.status === "string");

const renderPersonDocument = (label, document, fallbackUrl) => {
    if (!hasDocument(document, fallbackUrl)) return null;

    return (
        <span>
            {label}: <DocumentLink document={document} fallbackUrl={fallbackUrl} />
        </span>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="grid gap-1 border-b border-[#EAECF0] py-3 last:border-b-0 sm:grid-cols-[220px_1fr]">
        <div className="aku-body-normal-medium text-[#6F739B]">{label}</div>
        <div className="aku-body-normal-regular break-words text-[#161827]">
            {value || emptyValue}
        </div>
    </div>
);

const requirementLabels = {
    application_letter: "Application letter",
    bank_statement: "Bank statement",
    board_resolution: "Board resolution",
    business_address: "Business address",
    business_license: "Business license",
    business_name: "Business name",
    cac_form_1: "CAC BN Form 1",
    cac_form_2: "CAC Form 2",
    cac_form_2_1: "CAC Form 2.1",
    cac_form_3: "CAC Form 3",
    cac_form_4: "CAC Form 4",
    cac_form_7: "CAC Form 7",
    cac_status_report: "CAC Status Report",
    certificate_of_incorporation: "Certificate of incorporation",
    combined_cac_form: "Combined CAC form",
    commercial_registration_certificate: "Commercial registration certificate",
    directors: "Directors",
    memorandum_and_article_of_association: "Memorandum and Articles of Association",
    memorandum_articles: "Memorandum and Articles of Association",
    operating_agreement_or_articles_of_incorporation:
        "Operating Agreement or Articles of Incorporation",
    physical_address: "Physical address",
    proof_of_address: "Proof of address",
    registration_number: "Registration number",
    source_of_fund: "Source of funds",
    source_of_funds: "Source of funds",
    ubos: "Control person",
};

const formatRequirementLabel = (key = "") =>
    requirementLabels[normalizeKey(key)] ||
    String(key)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const findRequirement = (requirements, keys) => {
    const normalizedKeys = Array.isArray(keys) ? keys.map(normalizeKey) : [normalizeKey(keys)];
    return requirements.find((requirement) => normalizedKeys.includes(normalizeKey(requirement.key)));
};

const getRequirementText = (requirements, keys) => {
    const requirement = findRequirement(requirements, keys);
    return requirement?.textValue || requirement?.value || requirement?.stringValue || "";
};

const getRequirementObject = (requirements, keys) => {
    const requirement = findRequirement(requirements, keys);
    return requirement?.objectValue || requirement?.address || null;
};

const getRequirementDocument = (requirements, keys) => {
    const requirement = findRequirement(requirements, keys);
    return requirement?.document || requirement?.proofOfAddress || null;
};

const getRequirementPeople = (requirements, keys, field) => {
    const requirement = findRequirement(requirements, keys);
    return Array.isArray(requirement?.[field]) ? requirement[field] : [];
};

const getBusinessField = (business, field) => unwrapValue(business?.[field]);

const documentRequirementExclusions = new Set(["proof_of_address"]);

const getComplianceDocumentRequirements = (requirements) =>
    requirements.filter(
        (requirement) =>
            normalizeKey(requirement?.type) === "document" &&
            !documentRequirementExclusions.has(normalizeKey(requirement?.key)) &&
            (requirement?.document?.key || requirement?.document?.url)
    );

const PersonSummaryCard = ({ person, index, title }) => {
    const details = [
        formatName(person),
        person?.email,
        person?.phoneNumber,
        person?.title,
        formatOwnership(person) !== emptyValue ? `${formatOwnership(person)}% ownership` : null,
        person?.bvn || person?.bvnNumber ? `BVN: ${person?.bvn || person?.bvnNumber}` : null,
        person?.gender,
        person?.dateOfBirth || person?.dob ? `D.O.B: ${person?.dateOfBirth || person?.dob}` : null,
        formatIdentificationType(person) !== emptyValue ? formatIdentificationType(person) : null,
        formatIdentificationNumber(person) !== emptyValue
            ? `ID: ${formatIdentificationNumber(person)}`
            : null,
        renderPersonDocument("ID front", person?.idDocumentFront, person?.meanOfIdentificationUrl),
        renderPersonDocument("ID back", person?.idDocumentBack),
        formatPersonAddress(person) !== emptyValue ? formatPersonAddress(person) : null,
        typeof person?.isDirector === "boolean"
            ? `Also a director: ${person.isDirector ? "Yes" : "No"}`
            : null,
    ].filter(Boolean);

    return (
        <DetailRow
            label={`${title} ${index + 1}`}
            value={
                <div className="space-y-1">
                    {details.length ? (
                        details.map((detail, detailIndex) => (
                            <p key={`${title}-${index}-${detailIndex}`}>{detail}</p>
                        ))
                    ) : (
                        <p>{emptyValue}</p>
                    )}
                </div>
            }
        />
    );
};

const SummarySection = ({ title, editPath, children }) => {
    const navigate = useNavigate();

    return (
        <section className="rounded-lg bg-white p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
                <h4 className="text-lg font-semibold text-[#161827]">{title}</h4>
                {editPath && (
                    <button
                        type="button"
                        onClick={() => navigate(`${editPath}?returnTo=summary`)}
                        className="aku-body-normal-medium text-[#6F00FF] hover:underline"
                    >
                        Edit
                    </button>
                )}
            </div>
            <div>{children}</div>
        </section>
    );
};

const OnboardingSummary = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const businessId = useSelector((state) => state.businessid);
    const currentBusiness = useSelector((state) => state.business);
    const [summary, setSummary] = useState(null);
    const [business, setBusiness] = useState(null);
    const [allWallets, setAllWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const fetchBusiness = useCallback(async () => {
        if (!businessId) {
            setLoading(false);
            setErrorMessage("Unable to find business details. Please refresh and try again.");
            return;
        }

        try {
            setLoading(true);
            setErrorMessage("");
            const response = await handleFetchKybRequirementsSummary(businessId);
            const responseBody = getResponseBody(response);
            const submittedData =
                responseBody?.submittedData || responseBody?.business || responseBody;

            setSummary(responseBody);
            setBusiness(submittedData);
            setAllWallets(Array.isArray(responseBody?.allWallets) ? responseBody.allWallets : []);
        } catch (error) {
            setErrorMessage(error?.message || "Unable to load onboarding summary.");
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        fetchBusiness();
    }, [fetchBusiness]);

    const requirements = useMemo(
        () => (Array.isArray(summary?.kycRequirements) ? summary.kycRequirements : []),
        [summary?.kycRequirements]
    );

    const compliancePath = useMemo(() => {
        const type = business?.type;
        if (type === "SoleProprietorship") {
            return "/onboarding/business-verification/compliance/soleproprietorship";
        }
        return "/onboarding/business-verification/compliance/llc";
    }, [business?.type]);

    const walletSummary = useMemo(() => {
        const wallets = Array.isArray(business?.wallets) ? business.wallets : [];
        if (wallets.length > 0) {
            return wallets
                .map((wallet) => wallet.currency || wallet.name || wallet.flag)
                .filter(Boolean)
                .join(", ");
        }

        if (allWallets.length > 0) {
            return allWallets
                .map((wallet) => wallet.currency)
                .filter(Boolean)
                .join(", ");
        }

        return emptyValue;
    }, [allWallets, business?.wallets]);

    const complianceDocuments = useMemo(
        () => getComplianceDocumentRequirements(requirements),
        [requirements]
    );

    const summaryUbos = useMemo(() => {
        const requirementUbos = getRequirementPeople(requirements, ["ubos", "ubo"], "ubos");
        return requirementUbos.length ? requirementUbos : business?.UBOs || [];
    }, [business?.UBOs, requirements]);

    const summaryDirectors = useMemo(() => {
        const requirementDirectors = getRequirementPeople(
            requirements,
            ["directors", "director"],
            "directors"
        );
        return requirementDirectors.length ? requirementDirectors : business?.directors || [];
    }, [business?.directors, requirements]);

    const countryName =
        business?.businessCountry?.name ||
        business?.country?.name ||
        (typeof business?.country === "string" ? business.country : "");

    const submitForReview = async () => {
        try {
            setSubmitting(true);
            setErrorMessage("");
            const response = await handleRequestKybVerification();
            const responseBusiness = response?.business || response?.data?.business || null;
            const currentBusinessRecord =
                isBusinessRecord(currentBusiness)
                    ? currentBusiness
                    : {};
            const responseBusinessRecord = isBusinessRecord(responseBusiness) ? responseBusiness : {};
            const nextBusiness = {
                ...currentBusinessRecord,
                ...responseBusinessRecord,
                status: responseBusinessRecord?.status || "SUBMITTED",
            };

            dispatch({
                type: "BUSINESS",
                payload: nextBusiness,
            });
            sessionStorage.setItem("showOnboardingCongrats", "1");
            navigate("/dashboard");
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message ||
                    error?.message ||
                    "Something went wrong. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={5}
            showSkip={false}
            showStepper={false}
        >
            <div className="mx-auto max-w-[920px] pb-24">
                <div className="mb-8">
                    <h3 className="aku-heading-h2-medium text-[#101828]">Review your details</h3>
                    <p className="mt-2 aku-body-normal-regular text-[#6F739B]">
                        Confirm your onboarding information before submitting for review.
                    </p>
                </div>

                {errorMessage && <ErrorMessage message={errorMessage} />}

                {loading ? (
                    <div className="flex min-h-[240px] items-center justify-center">
                        <Loader />
                    </div>
                ) : (
                    <div className="space-y-5">
                        <SummarySection
                            title="Business details"
                            editPath="/onboarding/business-details"
                        >
                            <DetailRow label="Country" value={countryName} />
                            <DetailRow
                                label="Registration number"
                                value={
                                    getRequirementText(requirements, "registration_number") ||
                                    getBusinessField(business, "registrationNumber") ||
                                    business?.registrationNumberDetails?.value
                                }
                            />
                            <DetailRow
                                label="Tax identification"
                                value={business?.TIN?.value || business?.EIN?.value}
                            />
                            <DetailRow label="Business type" value={business?.type} />
                            <DetailRow
                                label="Business name"
                                value={
                                    getRequirementText(requirements, "business_name") ||
                                    getBusinessField(business, "name")
                                }
                            />
                            <DetailRow
                                label="Nature of business"
                                value={
                                    business?.natureOfBusiness?.nature ||
                                    business?.natureOfBusiness?.value ||
                                    business?.natureOfBusiness
                                }
                            />
                            <DetailRow
                                label="Business description"
                                value={business?.description || business?.businessDescription}
                            />
                            <DetailRow
                                label="High-risk activities"
                                value={
                                    business?.isHighRisk
                                        ? business?.activities?.join(", ")
                                        : "No"
                                }
                            />
                        </SummarySection>

                        <SummarySection
                            title="Contact details"
                            editPath="/onboarding/contact-details"
                        >
                            <DetailRow
                                label="Phone number"
                                value={
                                    getRequirementText(requirements, "phone_number") ||
                                    business?.phoneNumber
                                }
                            />
                            <DetailRow
                                label="Business email"
                                value={business?.email || business?.businessEmail}
                            />
                            <DetailRow
                                label="Business address"
                                value={formatAddress(
                                    getRequirementObject(requirements, "business_address") ||
                                        business?.businessAddress
                                )}
                            />
                            <DetailRow
                                label="Physical address"
                                value={formatAddress(
                                    getRequirementObject(requirements, "physical_address") ||
                                        business?.physicalAddress
                                )}
                            />
                            <DetailRow
                                label="Proof of address"
                                value={formatDocument(
                                    getRequirementDocument(requirements, "proof_of_address") ||
                                        business?.proofOfAddress
                                )}
                            />
                        </SummarySection>

                        <SummarySection title="Compliance" editPath={compliancePath}>
                            {complianceDocuments.length ? (
                                complianceDocuments.map((requirement) => (
                                    <DetailRow
                                        key={requirement.key}
                                        label={formatRequirementLabel(requirement.key)}
                                        value={formatDocument(requirement.document)}
                                    />
                                ))
                            ) : (
                                <>
                                    <DetailRow
                                        label="Business documents"
                                        value={
                                            business?.documents
                                                ? Object.keys(business.documents).join(", ")
                                                : emptyValue
                                        }
                                    />
                                    <DetailRow
                                        label="Bank statement"
                                        value={formatDocument(business?.documents?.BankStatement)}
                                    />
                                </>
                            )}
                        </SummarySection>

                        <SummarySection
                            title="Wallets"
                            editPath="/onboarding/business-verification/wallets"
                        >
                            <DetailRow label="Selected wallets" value={walletSummary} />
                            <DetailRow
                                label="Monthly transaction volume"
                                value={
                                    business?.monthlyTransactionVolume
                                        ? `${business.monthlyTransactionVolume.currency || ""} ${
                                              business.monthlyTransactionVolume.amount || ""
                                          }`
                                        : emptyValue
                                }
                            />
                            <DetailRow
                                label="Website"
                                value={
                                    business?.businessWebsite || (business?.hasWebsite ? "Yes" : "No")
                                }
                            />
                            <DetailRow
                                label="Source of funds"
                                value={formatDocument(
                                    business?.sourceOfFund ||
                                        getRequirementDocument(requirements, [
                                            "source_of_fund",
                                            "source_of_funds",
                                        ])
                                )}
                            />
                        </SummarySection>

                        <SummarySection
                            title="Control person"
                            editPath="/onboarding/business-ownership/control-person"
                        >
                            {summaryUbos?.length ? (
                                summaryUbos.map((ubo, index) => (
                                    <PersonSummaryCard
                                        key={ubo?._id || ubo?.email || index}
                                        person={ubo}
                                        index={index}
                                        title="Control person"
                                    />
                                ))
                            ) : (
                                <DetailRow label="Control persons" value={emptyValue} />
                            )}
                        </SummarySection>

                        <SummarySection
                            title="Directors"
                            editPath="/onboarding/business-ownership/directors"
                        >
                            {summaryDirectors?.length ? (
                                summaryDirectors.map((director, index) => (
                                    <PersonSummaryCard
                                        key={director?._id || director?.email || index}
                                        person={director}
                                        index={index}
                                        title="Director"
                                    />
                                ))
                            ) : (
                                <DetailRow label="Directors" value={emptyValue} />
                            )}
                        </SummarySection>

                        <div className="flex justify-end gap-4 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate("/onboarding/business-ownership/directors")}
                                className="rounded-lg px-6 py-3 text-sm font-semibold text-[#101828]"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={submitForReview}
                                disabled={submitting}
                                className={`rounded-lg px-6 py-3 text-sm font-semibold text-white ${
                                    submitting
                                        ? "cursor-not-allowed bg-[#E9D7FE]"
                                        : "bg-[#7F56D9] hover:bg-[#6f00ff]"
                                }`}
                            >
                                {submitting ? <Loader /> : "Submit for review"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </OnboardingLayout>
    );
};

export default OnboardingSummary;
